import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import SafeUrlAssembler from 'safe-url-assembler';
import { ApiEndpointService } from '../../core/services/api-endpoint.service';
import { Carriers } from '../../shared/enum/general-enum';
import { IAesParam } from '../models/aes-param.interface';
import { Commodity } from '../models/commodity.model';

type ShippingRoute = {
    from: string;
    to: string[];
};

type CarrierRoute = {
    carrier: Carriers;
    routes: ShippingRoute[];
};

/**
 * ShipmentAesService is a service that handles AES (Automated Export System)
 * submission and checks for shipments based on specific parameters and conditions.
 *
 * This service provides methods to:
 * - Submit shipment details to the AES system.
 * - Determine if a shipment should be submitted to AES.
 * - Check if EEI (Electronic Export Information) is required for a shipment.
 *
 * @param httpClient - HttpClient for making HTTP requests.
 * @param apiEndpointService - ApiEndpointService for managing API endpoints.
 */
@Injectable()
export class ShipmentAesService {
    private readonly EEI_COMMODITY_CODES = ['7102100000', '7102210000', '7102310000'];
    private readonly EEI_HIGH_VALUE_LIMIT = 2500;
    private readonly AES_FILING_OPTION_SELF = '2';

    public constructor(private readonly httpClient: HttpClient,
                       private readonly apiEndpointService: ApiEndpointService,
    ) {
    }

    /**
     * Submits the quote with the given ID and status to AES.
     *
     * @param {string} quoteId - The unique identifier of the quote to be submitted.
     * @param {number} status - The status value to be submitted.
     * @return {Observable<boolean>} An observable that emits a boolean indicating the success or failure of the submission.
     */
    public submitToAES(quoteId: string, status: number = 12): Observable<boolean> {
        const endpoint = SafeUrlAssembler().template(this.apiEndpointService.getEndpoint('submitToACE')).toString();
        return this.httpClient.put<boolean>(endpoint, { 'QuoteId': quoteId, 'Status': status });
    }

    /**
     * Determines whether the provided AES parameters should be submitted to AES.
     *
     * @param {IAesParam} aesParam - The AES parameters to be evaluated.
     * @return {boolean} - Returns true if the AES parameters should be submitted, otherwise false.
     ``` */
    public shouldSubmitToAes(aesParam: IAesParam): boolean {
        const isParamReady = this.isEveryParamValid(aesParam);
        if (!isParamReady) {
            return false;
        }

        return this.isEeiRequired(aesParam) && aesParam.eeiFilingOption !== this.AES_FILING_OPTION_SELF;
    }

    /**
     * Determines if EEI is required based on the provided AES parameters.
     *
     * @param {IAesParam} aesParam - The AES parameters including commodities, carrier, and shipping information.
     * @return {boolean} - Returns true if EEI is required, otherwise false.
     */
    public isEeiRequired(aesParam: IAesParam): boolean {
        if (this.containsEeiCommodity(aesParam.commodities)) {
            return this.isEeiCommodityRoute(aesParam.carrier, aesParam.shipFromCountryCode, aesParam.shipToCountryCode);
        }

        if (!this.hasHighValueCommodity(aesParam.commodities)) {
            return false;
        }

        return this.isEeiRoute(aesParam.carrier, aesParam.shipFromCountryCode, aesParam.shipToCountryCode);
    }

    /**
     * Checks if the given list of commodities contains any commodity
     * with an EEI (Electronic Export Information) Harmonized Code.
     *
     * @param {Commodity[]} commodities - The list of commodities to check.
     * @return {boolean} - Returns true if an EEI Harmonized Code is found in the list of commodities, otherwise false.
     */
    private containsEeiCommodity(commodities: Commodity[]): boolean {
        if (!commodities || commodities.length === 0) {
            return false;
        }

        return commodities.some(commodity => this.EEI_COMMODITY_CODES.includes(commodity.AESHarmonizedCode));
    }

    /**
     * Checks if the provided list of commodities contains at least one commodity
     * with a customs value exceeding the high value limit.
     *
     * @param {Commodity[]} commodityList - The list of commodities to check.
     * @return {boolean} - Returns true if there is at least one high value commodity, otherwise false.
     */
    private hasHighValueCommodity(commodityList: Commodity[]): boolean {
        return !!commodityList && commodityList.some(commodity => +commodity.CustomsValue > this.EEI_HIGH_VALUE_LIMIT);
    }

    /**
     * Checks if every parameter in the given IAesParam object is valid.
     *
     * @param {IAesParam} aesParam - The object containing AES parameters to be validated.
     * @return {boolean} - Returns true if all parameters are valid, otherwise false.
     */
    private isEveryParamValid(aesParam: IAesParam): boolean {
        return Object.keys(aesParam).every(key => !!aesParam[key]);
    }

    /**
     * Determines if a given shipment route requires EEI (Electronic Export Information) based on carrier and country codes.
     *
     * @param {Carriers} carrier - The carrier executing the shipment.
     * @param {string} shipFromCountryCode - The country code from where the shipment is originating.
     * @param {string} shipToCountryCode - The country code to where the shipment is being sent.
     * @return {boolean} - Returns true if the route requires EEI, otherwise false.
     */
    private isEeiRoute(carrier: Carriers, shipFromCountryCode: string, shipToCountryCode: string): boolean {
        const routes: CarrierRoute[] = [
            {
                carrier: Carriers.Ups,
                routes: [
                    { from: 'US', to: ['MX', 'PR', 'VI'] },
                ],
            },
            {
                carrier: Carriers.Fedex, routes: [
                    { from: 'US', to: ['MX', 'PR', 'VI'] },
                    { from: 'PR', to: ['US'] },
                    { from: 'VI', to: ['US'] },
                ],
            },
        ];
        return this.isCarrierRouteMatched(routes, carrier, shipFromCountryCode, shipToCountryCode);
    }

    /**
     * Determines if the given route is an EEI commodity route for the specified carrier.
     *
     * @param {Carriers} carrier - The carrier being used for the shipment (e.g., UPS, FedEx).
     * @param {string} shipFromCountryCode - The country code from which the shipment is originating.
     * @param {string} shipToCountryCode - The country code to which the shipment is destined.
     * @return {boolean} - Returns true if the specified route is an EEI commodity route for the carrier, false otherwise.
     */
    private isEeiCommodityRoute(carrier: Carriers, shipFromCountryCode: string, shipToCountryCode: string): boolean {
        const routes: CarrierRoute[] = [
            {
                carrier: Carriers.Ups,
                routes: [
                    { from: 'US', to: ['MX', 'PR', 'GU', 'CA', 'VI'] },
                    { from: 'CA', to: ['US'] },
                ],
            },
            {
                carrier: Carriers.Fedex,
                routes: [
                    { from: 'US', to: ['MX', 'PR', 'GU', 'CA', 'VI'] },
                    { from: 'PR', to: ['US'] },
                    { from: 'VI', to: ['US'] },
                    { from: 'CA', to: ['US'] },
                ],
            },
        ];
        return this.isCarrierRouteMatched(routes, carrier, shipFromCountryCode, shipToCountryCode);
    }

    /**
     * Determines if a carrier has a route that matches the given shipping origin and destination country codes.
     *
     * @param {CarrierRoute[]} routes - An array of carrier routes to check against.
     * @param {Carriers} carrier - The carrier whose routes need to be matched.
     * @param {string} shipFromCountryCode - The country code where the shipment originates.
     * @param {string} shipToCountryCode - The country code where the shipment is destined.
     * @return {boolean} - Returns true if a matching route is found; otherwise, returns false.
     */
    private isCarrierRouteMatched(routes: CarrierRoute[], carrier: Carriers, shipFromCountryCode: string, shipToCountryCode: string): boolean {
        const carrierRoutes = routes.find(route => route.carrier === carrier);
        if (!carrierRoutes) {
            return false;
        }

        const route = carrierRoutes.routes.find(route => route.from.toUpperCase() === shipFromCountryCode.toUpperCase());
        return route ? route.to.includes(shipToCountryCode) : false;
    }
}
